import React, { useState, useEffect } from 'react';
import { GitBranch, Search, Filter, Download, Calendar, User, FileText, TrendingUp, Activity, Loader2, AlertCircle, Eye, X } from 'lucide-react';

const DataLineage = () => {
  const [lineageData, setLineageData] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('all');
  const [filterChangeType, setFilterChangeType] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    fetchLineageData();
    fetchStatistics();
  }, []);

  const fetchLineageData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/data_lineage/get_lineage?limit=200');
      const data = await response.json();
      setLineageData(data.lineage || []);
    } catch (err) {
      setError('Failed to load lineage data');
      console.error('Error fetching lineage:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('/api/data_lineage/get_statistics');
      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'CREATE': return '#10b981';
      case 'UPDATE': return '#3b82f6';
      case 'DELETE': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getChangeTypeBg = (type) => {
    switch (type) {
      case 'CREATE': return '#d1fae5';
      case 'UPDATE': return '#dbeafe';
      case 'DELETE': return '#fee2e2';
      default: return '#f3f4f6';
    }
  };

  const getEntityTypeIcon = (type) => {
    switch (type) {
      case 'user': return '👤';
      case 'role': return '🔑';
      case 'persona': return '🎭';
      case 'question': return '❓';
      default: return '📄';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredData = lineageData.filter(entry => {
    const matchesSearch = 
      entry.entity_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.field_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.changed_by.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesEntityType = filterEntityType === 'all' || entry.entity_type === filterEntityType;
    const matchesChangeType = filterChangeType === 'all' || entry.change_type === filterChangeType;
    
    return matchesSearch && matchesEntityType && matchesChangeType;
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader2 style={{ width: '40px', height: '40px', color: 'var(--cares-blue)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header - Title removed to avoid duplication with Dashboard Header */}

      {/* Statistics Cards */}
      {statistics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <StatCard
            icon={<Activity style={{ width: '20px', height: '20px' }} />}
            label="Total Changes"
            value={statistics.total_changes}
            color="var(--cares-blue)"
          />
          <StatCard
            icon={<TrendingUp style={{ width: '20px', height: '20px' }} />}
            label="Last 24 Hours"
            value={statistics.recent_changes_24h}
            color="var(--cares-green)"
          />
          <StatCard
            icon={<User style={{ width: '20px', height: '20px' }} />}
            label="Active Users"
            value={statistics.top_users?.length || 0}
            color="var(--cares-purple)"
          />
          <StatCard
            icon={<FileText style={{ width: '20px', height: '20px' }} />}
            label="Entity Types"
            value={Object.keys(statistics.changes_by_entity || {}).length}
            color="var(--cares-amber)"
          />
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
          {/* Search */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
              <Search style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
              Search
            </label>
            <input
              type="text"
              placeholder="Search by entity, field, or user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--cares-line-card)',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Entity Type Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
              <Filter style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
              Entity Type
            </label>
            <select
              value={filterEntityType}
              onChange={(e) => setFilterEntityType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--cares-line-card)',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'var(--cares-surface-card)',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Types</option>
              <option value="user">Users</option>
              <option value="role">Roles</option>
              <option value="persona">Personas</option>
              <option value="question">Questions</option>
            </select>
          </div>

          {/* Change Type Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
              <Filter style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
              Change Type
            </label>
            <select
              value={filterChangeType}
              onChange={(e) => setFilterChangeType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--cares-line-card)',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'var(--cares-surface-card)',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Changes</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderRadius: '8px',
          color: 'var(--cares-orange)',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle style={{ width: '16px', height: '16px' }} />
          {error}
        </div>
      )}

      {/* Lineage Table */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--cares-line-card)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
            Change History
          </div>
          <div style={{ fontSize: '13px', color: 'var(--cares-text-label)' }}>
            Showing {filteredData.length} of {lineageData.length} changes
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '1px solid var(--cares-line-card)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Entity
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Field
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Old Value
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  New Value
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Change Type
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Changed By
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Date/Time
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: 'var(--cares-text-label)' }}>
                    No lineage data found
                  </td>
                </tr>
              ) : (
                filteredData.map((entry, index) => (
                  <tr
                    key={entry.id}
                    style={{
                      borderBottom: index < filteredData.length - 1 ? '1px solid var(--cares-line-card)' : 'none',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>{getEntityTypeIcon(entry.entity_type)}</span>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-2)' }}>
                            {entry.entity_name}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--cares-text-label)' }}>
                            {entry.entity_type}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--cares-text-3)', fontFamily: 'monospace' }}>
                      {entry.field_name}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--cares-text-label)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.old_value || <span style={{ fontStyle: 'italic', color: 'var(--cares-text-note)' }}>null</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--cares-text-2)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>
                      {entry.new_value}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: getChangeTypeBg(entry.change_type),
                        color: getChangeTypeColor(entry.change_type)
                      }}>
                        {entry.change_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--cares-text-3)' }}>
                      {entry.changed_by}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--cares-text-label)' }}>
                      {formatDate(entry.changed_at)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          setSelectedEntry(entry);
                          setShowDetailsModal(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: 'var(--cares-blue)',
                          color: 'var(--cares-text-max)',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                      >
                        <Eye style={{ width: '14px', height: '14px' }} />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedEntry && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            style={{
              background: 'var(--cares-surface-card)',
              borderRadius: '12px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--cares-line-card)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileText style={{ width: '20px', height: '20px', color: 'var(--cares-blue)' }} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-1)' }}>
                    Change Details
                  </h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--cares-text-label)' }}>
                    ID: {selectedEntry.id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'none'}
              >
                <X style={{ width: '20px', height: '20px', color: 'var(--cares-text-label)' }} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Entity Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Entity Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <DetailField label="Entity Type" value={selectedEntry.entity_type} />
                  <DetailField label="Entity ID" value={selectedEntry.entity_id} />
                  <DetailField label="Entity Name" value={selectedEntry.entity_name} span={2} />
                </div>
              </div>

              {/* Change Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Change Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <DetailField label="Field Name" value={selectedEntry.field_name} />
                  <DetailField
                    label="Change Type"
                    value={
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: getChangeTypeBg(selectedEntry.change_type),
                        color: getChangeTypeColor(selectedEntry.change_type)
                      }}>
                        {selectedEntry.change_type}
                      </span>
                    }
                  />
                </div>
              </div>

              {/* Value Changes */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Value Changes
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div style={{
                    padding: '16px',
                    background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
                    border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--cares-orange)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Old Value
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--cares-text-2)', fontFamily: 'monospace', wordBreak: 'break-word' }}>
                      {selectedEntry.old_value || <span style={{ fontStyle: 'italic', color: 'var(--cares-text-note)' }}>null</span>}
                    </div>
                  </div>
                  <div style={{
                    padding: '16px',
                    background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
                    border: '1px solid color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--cares-green)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      New Value
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--cares-text-2)', fontFamily: 'monospace', wordBreak: 'break-word' }}>
                      {selectedEntry.new_value || <span style={{ fontStyle: 'italic', color: 'var(--cares-text-note)' }}>null</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Metadata
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <DetailField label="Changed By" value={selectedEntry.changed_by} />
                  <DetailField label="Changed At" value={formatDate(selectedEntry.changed_at)} />
                  <DetailField label="Created At" value={formatDate(selectedEntry.created_at)} />
                  <DetailField
                    label="Change Reason"
                    value={selectedEntry.change_reason || <span style={{ fontStyle: 'italic', color: 'var(--cares-text-note)' }}>No reason provided</span>}
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--cares-line-card)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'var(--cares-blue)',
                  color: 'var(--cares-text-max)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => (
  <div style={{
    background: 'var(--cares-surface-card)',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }}>
    <div style={{
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--cares-text-2)', marginBottom: '4px' }}>
        {value}
      </div>
      <div style={{ fontSize: '13px', color: 'var(--cares-text-label)' }}>
        {label}
      </div>
    </div>
  </div>
);

// Detail Field Component for Modal
const DetailField = ({ label, value, span }) => (
  <div style={{ gridColumn: span ? `span ${span}` : 'auto' }}>
    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: '14px', color: 'var(--cares-text-2)', fontWeight: '500' }}>
      {value}
    </div>
  </div>
);

export default DataLineage;

